// dropdown.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import "./dropdown.css";

const DropdownMenu = ({
  ymapsApiRef,
  metroLineNumbers,

  // поиск №2 (адрес -> ближайшее метро)
  setNewCoords,
  mapRef,
  linecoordinates,
  coordinates,
  toggleActiveStation,
  selectOrFocusStation,
  toggleActiveLine,
  activeIds,
  calculateDistances,
  setClosestCoords,
  metroLineColors,
  setActiveIds,
  setVisible,
  visible,
  userState,

  canViewBuildings,
  canUseNavigation,
  mainOck,
  canViewOck,
  canManageOck,
  canViewArchiveBuildings,

  // поиск №1 (Яндекс точка)
  onYandexPoint,

  // поиск №3 (станции)
  onStationPick,

  mobileToolsOpen,
  onMobileToolsToggle
}) => {
  // ------------------------------------------------------------
  // UI: меню/панели
  // ------------------------------------------------------------
  const menuRef = useRef(null);
  const subRef = useRef(null);
  const filterRef = useRef(null);
  const diamRef = useRef(null);

  const searchWrapRef = useRef(null);
  const warnSuggestRef = useRef(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [isDiametersMenuOpen, setIsDiametersMenuOpen] = useState(false);

  // ------------------------------------------------------------
  //  Мобильная адаптация
  // ------------------------------------------------------------
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();

    // safari < 14
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  // ------------------------------------------------------------
  //  Тип поиска
  // ------------------------------------------------------------
  const SEARCH = {
    YANDEX: "yandex", // (1)
    METRO: "metro", // (2)
    STATIONS: "stations", // (3)
  };

  const [searchMode, setSearchMode] = useState(SEARCH.YANDEX);

  // единый инпут
  const [q, setQ] = useState("");

  // подсказки (для yandex/metro) — из YM suggest/geocode
  const [addrOptions, setAddrOptions] = useState([]); // [{label, coords:null}]
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrHi, setAddrHi] = useState(-1);
  const addrReqIdRef = useRef(0);

  // подсказки (для stations) — локально из coordinates.details
  const [stOpen, setStOpen] = useState(false);
  const [stHi, setStHi] = useState(-1);

  // ------------------------------------------------------------
  // helpers close
  // ------------------------------------------------------------
  const closeAll = useCallback(() => {
    setIsMenuOpen(false);
    setSubMenuOpen(false);
    setIsFilterMenuOpen(false);
    setIsDiametersMenuOpen(false);

    setAddrOpen(false);
    setAddrHi(-1);

    setStOpen(false);
    setStHi(-1);
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (
        !menuRef.current?.contains(t) &&
        !subRef.current?.contains(t) &&
        !filterRef.current?.contains(t) &&
        !diamRef.current?.contains(t) &&
        !searchWrapRef.current?.contains(t)
      ) {
        closeAll();
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") closeAll();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [closeAll, isMobile, mobileToolsOpen, onMobileToolsToggle]);

  const toggleMenu = () => {
    const next = !isMenuOpen;
    closeAll();
    setIsMenuOpen(next);
  };
  const toggleSubMenu = () => {
    const next = !subMenuOpen;
    closeAll();
    setSubMenuOpen(next);
  };
  const toggleFilterMenu = () => {
    const next = !isFilterMenuOpen;
    closeAll();
    setIsFilterMenuOpen(next);
  };
  const toggleDiametersMenu = () => {
    const next = !isDiametersMenuOpen;
    closeAll();
    setIsDiametersMenuOpen(next);
  };

  // ------------------------------------------------------------
  // YMaps helpers: suggest + geocode
  // ------------------------------------------------------------
  const geocodeFull = async (text, results = 1) => {
    const api = ymapsApiRef?.current;
    if (!api) throw new Error("YMaps API not loaded yet");
    const res = await api.geocode(text, { results });
    return res;
  };

  const geocodeByText = async (text) => {
    const res = await geocodeFull(text, 1);
    const first = res?.geoObjects?.get?.(0);
    if (!first) return null;
    return first.geometry.getCoordinates(); // [lat, lon]
  };

  const suggestByText = async (text) => {
    const api = ymapsApiRef?.current;
    if (!api) return [];

    const s = String(text || "").trim();
    if (s.length < 2) return [];

    if (typeof api.suggest === "function") {
      try {
        const items = await api.suggest(s, { results: 7 });
        const out = (items || [])
          .map((it) => ({
            label: it?.displayName || it?.value || "",
            coords: null,
          }))
          .filter((x) => x.label);

        if (out.length) return out;
      } catch (e) {
        if (!warnSuggestRef.current) {
          warnSuggestRef.current = true;
          // eslint-disable-next-line no-console
          console.warn("YMaps suggest failed (YMError). Fallback to geocode will be used.");
        }
      }
    }

    try {
      const res = await api.geocode(s, { results: 7 });
      const geoObjects = res?.geoObjects;
      if (!geoObjects) return [];

      const out = [];
      const n = geoObjects.getLength();
      for (let i = 0; i < n; i++) {
        const obj = geoObjects.get(i);
        if (!obj) continue;
        const name =
          obj.getAddressLine?.() ||
          obj.properties?.get?.("text") ||
          obj.properties?.get?.("name") ||
          "";
        if (!name) continue;
        out.push({ label: name, coords: null });
      }
      return out;
    } catch {
      return [];
    }
  };

  // ------------------------------------------------------------
  // ✅ Поиск №1: Яндекс (ставит точку и показывает инфу)
  // ------------------------------------------------------------
  const applyYandexPoint = useCallback(
    async (label) => {
      const txt = String(label || "").trim();
      if (!txt) return;

      setQ(txt);
      setAddrOpen(false);
      setAddrHi(-1);

      try {
        const res = await geocodeFull(txt, 1);
        const first = res?.geoObjects?.get?.(0);
        if (!first) return;

        const coords = first.geometry.getCoordinates(); // [lat, lon]
        const address =
          first.getAddressLine?.() ||
          first.properties?.get?.("text") ||
          first.properties?.get?.("name") ||
          txt;

        const meta = first.properties?.get?.("metaDataProperty") || null;
        const desc =
          first.properties?.get?.("description") ||
          meta?.GeocoderMetaData?.text ||
          "";

        if (mapRef?.current?.setCenter) mapRef.current.setCenter(coords, 16);

        onYandexPoint?.({
          coords,
          address,
          description: desc,
          raw: meta,
          query: txt,
        });
      } catch {}
    },
    [mapRef, onYandexPoint] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ------------------------------------------------------------
  // ✅ Поиск №2: Адрес -> ближайшее метро/участок
  // ------------------------------------------------------------
  const applyMetroPoint = useCallback(
    async (label) => {
      const txt = String(label || "").trim();
      if (!txt) return;

      setQ(txt);
      setAddrOpen(false);
      setAddrHi(-1);

      try {
        const coords = await geocodeByText(txt);
        if (!coords || coords.length !== 2) return;

        setNewCoords?.(coords);
        setClosestCoords?.(null);

        if (mapRef?.current?.setCenter) mapRef.current.setCenter(coords, 16);
        calculateDistances?.(coords);
      } catch {}
    },
    [mapRef, setNewCoords, setClosestCoords, calculateDistances] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ------------------------------------------------------------
  // ✅ Поиск №3: станции — локальный поиск + отображение ветки
  // ------------------------------------------------------------
  const normalizeLineName = (line) => String(line || "").replace(/ё/g, "е");

  const lineColorHex = (line) => {
    const ln = normalizeLineName(line);
    const hex = metroLineColors?.[ln] || metroLineColors?.[line] || "000000";
    return `#${String(hex).replace("#", "")}`;
  };

  const stationSearchOptions = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = Array.isArray(coordinates?.details) ? coordinates.details : [];
    if (!s) return arr.slice(0, 30);
    return arr
      .filter((st) => String(st?.name || "").toLowerCase().includes(s))
      .slice(0, 50);
  }, [q, coordinates]);

  const pickStation = (station) => {
    if (!station) return;

    setQ(station.name || "");
    setStOpen(false);
    setStHi(-1);

    if (station?.id != null) {
      selectOrFocusStation?.(station.id);
    }
    onStationPick?.(station);
  };

  // ------------------------------------------------------------
  // Suggestions loader for address-based modes (yandex/metro)
  // ------------------------------------------------------------
  useEffect(() => {
    const text = q.trim();

    if (searchMode === SEARCH.STATIONS) {
      setAddrOptions([]);
      setAddrOpen(false);
      setAddrLoading(false);
      setAddrHi(-1);
      return;
    }

    if (text.length < 2) {
      setAddrOptions([]);
      setAddrOpen(false);
      setAddrLoading(false);
      setAddrHi(-1);
      return;
    }

    let alive = true;
    const myReqId = ++addrReqIdRef.current;
    setAddrLoading(true);

    const t = setTimeout(() => {
      (async () => {
        try {
          const opts = await suggestByText(text);
          if (!alive || myReqId !== addrReqIdRef.current) return;
          setAddrOptions(opts);
          setAddrOpen(true);
          setAddrHi(opts.length ? 0 : -1);
        } catch {
          if (!alive || myReqId !== addrReqIdRef.current) return;
          setAddrOptions([]);
          setAddrOpen(false);
          setAddrHi(-1);
        } finally {
          if (alive && myReqId === addrReqIdRef.current) setAddrLoading(false);
        }
      })();
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, searchMode, ymapsApiRef]);

  // ------------------------------------------------------------
  // Enter handler (depends on mode)
  // ------------------------------------------------------------
  const handleEnter = async () => {
    const text = q.trim();
    if (!text) return;

    if (searchMode === SEARCH.STATIONS) {
      const st = stOpen && stHi >= 0 ? stationSearchOptions[stHi] : null;
      if (st) pickStation(st);
      return;
    }

    if (addrOpen && addrHi >= 0 && addrOptions[addrHi]) {
      const picked = addrOptions[addrHi].label;
      if (searchMode === SEARCH.YANDEX) await applyYandexPoint(picked);
      else await applyMetroPoint(picked);
      return;
    }

    if (searchMode === SEARCH.YANDEX) await applyYandexPoint(text);
    else await applyMetroPoint(text);
  };

  // ------------------------------------------------------------
  // переключение режима (select)
  // ------------------------------------------------------------
  const switchMode = (mode) => {
    setSearchMode(mode);
    setAddrOptions([]);
    setAddrOpen(false);
    setAddrLoading(false);
    setAddrHi(-1);
    setStOpen(false);
    setStHi(-1);

    // подчистим прошлые эффекты
    if (mode === SEARCH.YANDEX) {
      setNewCoords?.(null);
      setClosestCoords?.(null);
    } else if (mode === SEARCH.METRO) {
      onYandexPoint?.(null);
    }
  };

  const clearSearch = () => {
    setQ("");
    setAddrOptions([]);
    setAddrOpen(false);
    setAddrHi(-1);
    setStOpen(false);
    setStHi(-1);

    if (searchMode === SEARCH.YANDEX) onYandexPoint?.(null);
    if (searchMode === SEARCH.METRO) {
      setNewCoords?.(null);
      setClosestCoords?.(null);
    }
  };

  // ------------------------------------------------------------
  // ЛИНИИ / СТАНЦИИ панели (как было)
  // ------------------------------------------------------------
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedDiameters, setSelectedDiameters] = useState([]);

  const handleClearLines = () => {
    setActiveIds((prev) => ({ ...prev, activeLines: [], activeStations: [] }));
    setSelectedLines([]);
    setSelectedDiameters([]);
  };

  const groupedLines = useMemo(() => {
    const details = Array.isArray(linecoordinates?.details) ? linecoordinates.details : [];
    return details.reduce((acc, line) => {
      if (!acc[line.line]) acc[line.line] = [];
      acc[line.line].push(line.id);
      return acc;
    }, {});
  }, [linecoordinates]);

  const sortedLines = useMemo(() => {
    const uniqueLines = Object.keys(groupedLines);
    return [...uniqueLines].sort((a, b) => {
      const lineNumberA = metroLineNumbers[a] ?? Infinity;
      const lineNumberB = metroLineNumbers[b] ?? Infinity;
      return lineNumberA - lineNumberB;
    });
  }, [groupedLines, metroLineNumbers]);

  const handleLineSelect = (lineName) => {
    toggleActiveLine(groupedLines[lineName]);

    const isSelected = selectedLines.includes(lineName);
    setSelectedLines(isSelected ? selectedLines.filter((l) => l !== lineName) : [...selectedLines, lineName]);
  };

  const handleStationSelectFromPanel = (station) => {
    toggleActiveStation(station.id);
    setSubMenuOpen(false);
  };

  // ====== Область видимости ======
  const handleVisibilityToggle = (item) => {
    if (item === "buildings" && !canViewBuildings) return;
    if (item === "archiveBuildings" && !canViewArchiveBuildings) return;
    setVisible((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  };

  const resetVisibility = () => {
    setVisible([]);
    setIsFilterMenuOpen(false);
  };

  // ====== Диаметры ======
  const uniq = (arr) => Array.from(new Set(arr));
  const addIds = (base, ids) => uniq([...(base || []), ...(ids || [])]);
  const removeIds = (base, ids) => {
    const rm = new Set(ids || []);
    return (base || []).filter((x) => !rm.has(x));
  };

  const clearDiameters = () => {
    setActiveIds((p) => ({
      ...p,
      activeLines: removeIds(p.activeLines, selectedDiameters.flatMap((d) => groupedLines[d] || [])),
    }));
    setSelectedDiameters([]);
  };

  const handleDiameterSelect = (diameter) => {
    const ids = groupedLines[diameter] || [];
    if (!ids.length) return;

    setSelectedDiameters((prev) => {
      const isOn = prev.includes(diameter);
      const next = isOn ? prev.filter((d) => d !== diameter) : [...prev, diameter];

      setActiveIds((p) => ({
        ...p,
        activeLines: isOn ? removeIds(p.activeLines, ids) : addIds(p.activeLines, ids),
        activeStations: [],
      }));

      return next;
    });
  };


  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  const placeholder =
    searchMode === SEARCH.YANDEX
      ? "Яндекс поиск…"
      : searchMode === SEARCH.METRO
      ? "Адрес → ближайшее метро…"
      : "Станции…";

  const isOpen = !isMobile || mobileToolsOpen;
  
  return (
    <>
      <div
        className={isMobile ? `dd-mobileOverlay ${isOpen ? "dd-mobileOverlay--open" : ""}` : ""}
        style={isMobile ? { pointerEvents: "none" } : undefined} //  карта кликается
      >
        <div
          className={`dd-toolbar ${isMobile ? "dd-toolbar--mobile" : ""}`}
          id="topToolbar"
          style={isMobile ? { pointerEvents: "auto" } : undefined} //  клики только по панели
        >
          {isMobile && (
            <div className="dd-head">
              <div className="dd-title">Навигация</div>
              <button
                type="button"
                className="dd-close"
                onClick={onMobileToolsToggle}
                aria-label="Закрыть"
                title="Закрыть"
              >
                ✕
              </button>
            </div>
          )}

          {/* ===================== ПОИСК: select + input + clear ===================== */}
          {(canUseNavigation || canViewArchiveBuildings) && (
            <div className="dd-searchWrap dd-searchWrap--compact" ref={searchWrapRef}>
              <div className="dd-searchBar">
                <select
                  className="dd-searchSelect"
                  value={searchMode}
                  onChange={(e) => switchMode(e.target.value)}
                  aria-label="Тип поиска"
                  title="Тип поиска"
                >
                  <option value={SEARCH.YANDEX}>Яндекс</option>
                  <option value={SEARCH.METRO}>Метро рядом</option>
                  <option value={SEARCH.STATIONS}>Станции</option>
                </select>

                <input
                  className="dd-search dd-search--compact"
                  type="text"
                  value={q}
                  placeholder={placeholder}
                  autoComplete="off"
                  onFocus={() => {
                    if (searchMode === SEARCH.STATIONS) setStOpen(true);
                    else if (addrOptions.length > 0) setAddrOpen(true);
                  }}
                  onChange={(e) => {
                    setQ(e.target.value);
                    if (searchMode === SEARCH.STATIONS) {
                      setStOpen(true);
                      setStHi(0);
                    }
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      if (searchMode === SEARCH.STATIONS) {
                        if (!stOpen) setStOpen(true);
                        setStHi((prev) => {
                          const n = stationSearchOptions.length;
                          if (!n) return -1;
                          return prev < 0 ? 0 : Math.min(prev + 1, n - 1);
                        });
                      } else {
                        if (!addrOpen) setAddrOpen(true);
                        setAddrHi((prev) => {
                          const n = addrOptions.length;
                          if (!n) return -1;
                          return prev < 0 ? 0 : Math.min(prev + 1, n - 1);
                        });
                      }
                      return;
                    }

                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      if (searchMode === SEARCH.STATIONS) {
                        setStHi((prev) => {
                          const n = stationSearchOptions.length;
                          if (!n) return -1;
                          return prev <= 0 ? 0 : prev - 1;
                        });
                      } else {
                        setAddrHi((prev) => {
                          const n = addrOptions.length;
                          if (!n) return -1;
                          return prev <= 0 ? 0 : prev - 1;
                        });
                      }
                      return;
                    }

                    if (e.key === "Enter") {
                      e.preventDefault();
                      await handleEnter();
                      return;
                    }

                    if (e.key === "Escape") {
                      e.preventDefault();
                      setAddrOpen(false);
                      setAddrHi(-1);
                      setStOpen(false);
                      setStHi(-1);
                    }
                  }}
                />

                <button
                  className="dd-clearSide"
                  type="button"
                  onClick={clearSearch}
                  aria-label="Очистить поиск"
                  title="Очистить"
                >
                  ✖
                </button>

                {/* ---------- Подсказки: YANDEX / METRO ---------- */}
                {searchMode !== SEARCH.STATIONS && (addrOpen || addrLoading) && (
                  <div className="dd-suggest dd-suggest--compact">
                    {addrLoading && <div className="dd-suggestItem dd-suggestMuted">Поиск…</div>}

                    {!addrLoading && addrOptions.length === 0 && q.trim().length >= 2 && (
                      <div className="dd-suggestItem dd-suggestMuted">Ничего не найдено</div>
                    )}

                    {!addrLoading &&
                      addrOptions.map((it, idx) => (
                        <button
                          key={`${it.label}-${idx}`}
                          type="button"
                          className={`dd-suggestItem ${idx === addrHi ? "dd-suggestItem--active" : ""}`}
                          onMouseEnter={() => setAddrHi(idx)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => (searchMode === SEARCH.YANDEX ? applyYandexPoint(it.label) : applyMetroPoint(it.label))}
                          title={it.label}
                        >
                          {it.label}
                        </button>
                      ))}
                  </div>
                )}

                {/* ---------- Подсказки: STATIONS ---------- */}
                {searchMode === SEARCH.STATIONS && stOpen && (
                  <div className="dd-suggest dd-suggest--compact">
                    {stationSearchOptions.length === 0 && <div className="dd-suggestItem dd-suggestMuted">Ничего не найдено</div>}

                    {stationSearchOptions.map((st, idx) => {
                      const lineName = st?.line || "";
                      const c = lineColorHex(lineName);
                      const ln = normalizeLineName(lineName);
                      const badge = metroLineNumbers?.[ln] ?? metroLineNumbers?.[lineName] ?? "";

                      return (
                        <button
                          key={st?.id ?? `${st?.name}-${idx}`}
                          type="button"
                          className={`dd-suggestItem ${idx === stHi ? "dd-suggestItem--active" : ""}`}
                          onMouseEnter={() => setStHi(idx)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickStation(st)}
                          title={st?.name || ""}
                          style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                        >
                          <span style={{ width: 10, height: 10, borderRadius: 999, background: c, marginTop: 4 }} />

                          {badge !== "" && (
                            <span
                              style={{
                                minWidth: 22,
                                height: 18,
                                borderRadius: 9,
                                padding: "0 6px",
                                border: `1px solid ${c}`,
                                color: c,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                lineHeight: "18px",
                                marginTop: 1,
                              }}
                            >
                              {badge}
                            </span>
                          )}

                          <span style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
                            <span style={{ color: c, fontWeight: 700, lineHeight: "16px" }}>{st?.name || ""}</span>
                            <span style={{ color: "#666", fontSize: 12, lineHeight: "14px" }}>{lineName || "Ветка не указана"}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===================== ЛИНИИ ===================== */}
          {canUseNavigation && (
            <div
              ref={menuRef}
              tabIndex={-1}
              className="dd-menu"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setIsMenuOpen(false);
              }}
            >
              <button className="dd-btn dd-btn-primary dd-btn-wide" type="button" onClick={toggleMenu}>
                Выберите линии <span className="dd-caret">▼</span>
              </button>

              {isMenuOpen && (
                <div
                  className="dd-panel dd-panel-lines"
                  onMouseLeave={isMobile ? undefined : () => setIsMenuOpen(false)}
                >
                  <button
                    type="button"
                    className="dd-item dd-item-reset"
                    onClick={() => {
                      handleClearLines();
                      setIsMenuOpen(false);
                    }}
                  >
                    <span className={`dd-check ${selectedLines.length === 0 ? "dd-check--on" : ""}`} />
                    <span>Сбросить выбор</span>
                  </button>

                  <div className="dd-list">
                    {sortedLines.map((line) => {
                      const isOn = selectedLines.includes(line);
                      const badgeColor = `#${metroLineColors?.[line] ?? "000000"}`;
                      return (
                        <button key={line} type="button" className="dd-item" onClick={() => handleLineSelect(line)}>
                          <span className={`dd-check ${isOn ? "dd-check--on" : ""}`} />
                          <span className="dd-itemText">
                            <span className="dd-lineBadge" style={{ "--badgeColor": badgeColor }}>
                              {metroLineNumbers?.[line] ?? ""}
                            </span>
                            {line}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===================== СТАНЦИИ (панель по выбранным линиям) ===================== */}
          {canUseNavigation && (
            <div
              ref={subRef}
              tabIndex={-1}
              className="dd-menu"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setSubMenuOpen(false);
              }}
            >
              <button
                type="button"
                className={`dd-btn dd-btn-success dd-btn-stations ${selectedLines.length === 0 ? "dd-btn-disabled" : ""}`}
                onClick={toggleSubMenu}
                disabled={selectedLines.length === 0}
              >
                Выберите станции <span className="dd-caret">▼</span>
              </button>

              {subMenuOpen && selectedLines.length > 0 && (
                <div className="dd-panel dd-panel-stations" onMouseLeave={() => setSubMenuOpen(false)}>
                  {selectedLines.map((line) => {
                    const stations = (coordinates?.details ?? []).filter((s) => s.line === line);
                    const c = lineColorHex(line);

                    return (
                      <div key={line} className="dd-stCol">
                        <h4 className="dd-stColTitle">{line}</h4>

                        <button
                          type="button"
                          className="dd-item dd-item-reset"
                          onClick={() => setActiveIds((prev) => ({ ...prev, activeStations: [] }))}
                        >
                          <span className={`dd-check ${(activeIds?.activeStations?.length ?? 0) === 0 ? "dd-check--on" : ""}`} />
                          <span>Сбросить выбор</span>
                        </button>

                        <div className="dd-stList">
                          {stations.map((station) => {
                            const on = activeIds?.activeStations?.includes(station.id);
                            return (
                              <button
                                key={station.id}
                                type="button"
                                className="dd-stItem"
                                onClick={() => handleStationSelectFromPanel(station)}
                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                              >
                                <span className={`dd-check ${on ? "dd-check--on" : ""}`} />
                                <span style={{ width: 10, height: 10, borderRadius: 999, background: c }} />
                                <span className="dd-stName" style={{ color: c, fontWeight: 600 }}>
                                  {station.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ===================== ОБЛАСТЬ ВИДИМОСТИ ===================== */}
          {(canUseNavigation || canViewArchiveBuildings) && (
            <div
              ref={filterRef}
              tabIndex={-1}
              className="dd-menu"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setIsFilterMenuOpen(false);
              }}
            >
              <button className="dd-btn dd-btn-primary dd-btn-wide" type="button" onClick={toggleFilterMenu}>
                Область видимости <span className="dd-caret">▼</span>
              </button>

              {isFilterMenuOpen && (
                <div className="dd-panel dd-panel-filter" onMouseLeave={() => setIsFilterMenuOpen(false)}>
                  <button type="button" className="dd-item dd-item-reset" onClick={resetVisibility}>
                    <span className={`dd-check ${visible.length === 0 ? "dd-check--on" : ""}`} />
                    <span>Сбросить выбор</span>
                  </button>

                  <div className="dd-list">
                    {canUseNavigation && (
                      <>
                        <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("lines")}>
                          <span className={`dd-check ${visible.includes("lines") ? "dd-check--on" : ""}`} />
                          <span className="dd-itemText">Линии метро и съезды</span>
                        </button>

                        <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("stations")}>
                          <span className={`dd-check ${visible.includes("stations") ? "dd-check--on" : ""}`} />
                          <span className="dd-itemText">Станции метро и выходы</span>
                        </button>

                        <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("hermodoors")}>
                          <span className={`dd-check ${visible.includes("hermodoors") ? "dd-check--on" : ""}`} />
                          <span className="dd-itemText">Гермозатворы</span>
                        </button>
                      </>
                    )}

                    {canViewBuildings && (
                      <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("buildings")}>
                        <span className={`dd-check ${visible.includes("buildings") ? "dd-check--on" : ""}`} />
                        <span className="dd-itemText">Здания</span>
                      </button>
                    )}

                    {userState?.userState && (
                      <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("withDocs")}>
                        <span className={`dd-check ${visible.includes("withDocs") ? "dd-check--on" : ""}`} />
                        <span className="dd-itemText">С документацией</span>
                      </button>
                    )}

                    {canViewArchiveBuildings && (
                      <button type="button" className="dd-item" onClick={() => handleVisibilityToggle("archiveBuildings")}>
                        <span className={`dd-check ${visible.includes("archiveBuildings") ? "dd-check--on" : ""}`} />
                        <span className="dd-itemText">Архивы</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===================== ДИАМЕТРЫ ===================== */}
          {canUseNavigation && (
            <div
              ref={diamRef}
              tabIndex={-1}
              className="dd-menu"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setIsDiametersMenuOpen(false);
              }}
            >
              <button className="dd-btn dd-btn-primary dd-btn-wide" type="button" onClick={toggleDiametersMenu}>
                Выберите диаметры <span className="dd-caret">▼</span>
              </button>

              {isDiametersMenuOpen && (
                <div className="dd-panel dd-panel-diam" onMouseLeave={() => setIsDiametersMenuOpen(false)}>
                  <button
                    type="button"
                    className="dd-item dd-item-reset"
                    onClick={() => {
                      clearDiameters();
                      setIsDiametersMenuOpen(false);
                    }}
                  >
                    <span className={`dd-check ${selectedDiameters.length === 0 ? "dd-check--on" : ""}`} />
                    <span>Сбросить выбор</span>
                  </button>

                  <div className="dd-list">
                    {["D1", "D2", "D3", "D4"].map((diameter) => {
                      const on = selectedDiameters.includes(diameter);
                      return (
                        <button key={diameter} type="button" className="dd-item" onClick={() => handleDiameterSelect(diameter)}>
                          <span className={`dd-check ${on ? "dd-check--on" : ""}`} />
                          <span className="dd-itemText">{diameter}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===================== СПИСОК ЗАДАЧ ===================== */}
          {canViewOck && (
            <div
              tabIndex={-1}
              className="dd-menu"
            >
              <button className="dd-btn dd-btn-primary dd-btn-wide" type="button">
                <Link to="/tasks" className="dd-link">
                  Список задач
                </Link>
              </button>
            </div>
          )}

          {/* ===================== ОБЛАКО ===================== */}
          {canViewOck && (
            <div
              tabIndex={-1}
              className="dd-menu"
            >
              <button className="dd-btn dd-btn-primary dd-btn-wide" type="button">
                <Link to="/cloud" className="dd-link">
                  Облако
                </Link>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DropdownMenu;
